import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { trackShareComplete, trackShareOpen } from './ga';
import { ShareModal } from './components/ShareModal';
import {
  copyShareUrl,
  isMobileShareViewport,
  nativeShare,
  resolveSharePayload,
} from './sharePayload';

/** @type {import('./sharePayload').SharePayloadOverride | null} */
const ShareContext = createContext(null);

export function ShareProvider({ children }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  /** @type {[import('./sharePayload').SharePayloadOverride | null, Function]} */
  const [override, setOverride] = useState(null);

  const payload = useMemo(
    () => resolveSharePayload(location, override),
    [location, override],
  );

  /** @type {(custom?: import('./sharePayload').SharePayloadOverride) => void} */
  const openShareModal = useCallback(async (custom) => {
    const shareLocation = custom?.location ?? 'unknown';
    trackShareOpen(shareLocation);

    const nextPayload = resolveSharePayload(location, custom ?? null);

    if (isMobileShareViewport()) {
      const result = await nativeShare(nextPayload);
      if (result === 'native') {
        trackShareComplete('native', shareLocation);
      } else if (result === 'cancelled') {
        trackShareComplete('cancelled', shareLocation);
      } else {
        try {
          const copied = await copyShareUrl(nextPayload.url);
          if (copied) {
            trackShareComplete('clipboard', shareLocation);
          }
        } catch {
          /* clipboard unavailable */
        }
      }
      return;
    }

    setOverride(custom ?? null);
    setOpen(true);
  }, [location]);

  const closeShareModal = useCallback(() => {
    setOpen(false);
    setOverride(null);
  }, []);

  const value = useMemo(
    () => ({ openShareModal, closeShareModal }),
    [openShareModal, closeShareModal],
  );

  return (
    <ShareContext.Provider value={value}>
      {children}
      {open && (
        <ShareModal
          payload={payload}
          shareLocation={override?.location ?? 'unknown'}
          onClose={closeShareModal}
        />
      )}
    </ShareContext.Provider>
  );
}

export function useShare() {
  const ctx = useContext(ShareContext);
  if (ctx == null) {
    throw new Error('useShare must be used within ShareProvider');
  }
  return ctx;
}
